import { useMemo } from 'react'
import { ServerApiClient } from '../../../data/ServerApiClient'
import { CfoCockpitPage } from './CfoCockpitPage'
import { resolveCfoPageFromNavId } from './cfoNav'
import { buildCfoRoutePath, cfoPageFromRoute, withFirmCockpitContext } from './cfoRoutes'
import { CfoFirmNavigationProvider, useCfoFirmNavigation } from './CfoFirmNavigation'
import { CfoPageShell, CfoShellSelectChip, type CfoShellSelectOption } from './CfoPageShell'
import { TemplatesPage } from '../../views/TemplatesPage' // FE:C7
import { createViewsApi } from '../../views/viewsApi' // FE:C7

const siteSlugFromLocation = (): string => {
  if (typeof window === 'undefined') return ''
  const first = window.location.pathname.split('/').filter(Boolean)[0] || ''
  try {
    return decodeURIComponent(first)
  } catch {
    return first
  }
}

const navigateInApp = (target: string) => {
  if (typeof window === 'undefined' || !target || target === '/') return
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (current === target) return
  window.history.pushState(window.history.state, '', target)
  window.dispatchEvent(new Event('fg_route_change'))
}

// FE:C7 — firm admin → Templates (advisor pack templates, moved from the static Data Studio).
const firmSubpage = (): string => (typeof window === 'undefined' ? '' : window.location.pathname.split('/').filter(Boolean)[2] || '')

function CfoFirmCockpitShell() {
  const navigation = useCfoFirmNavigation()
  const viewsApi = useMemo(() => createViewsApi(), [])
  const templatesOpen = firmSubpage() === 'templates'
  const companyOptions = useMemo<CfoShellSelectOption[]>(
    () => navigation.clients.map((client) => ({
      id: client.workbook_id,
      label: client.company_name || client.client_name || 'CFO Core',
    })),
    [navigation.clients],
  )

  const openSelectedModule = (navId: string) => {
    const route = resolveCfoPageFromNavId(navId)
    const client = navigation.selectedClient
    if (!route || !client) return
    navigateInApp(withFirmCockpitContext(buildCfoRoutePath({
      siteSlug: client.site_slug || navigation.siteSlug,
      workbookId: client.workbook_id,
      page: cfoPageFromRoute(route),
    })))
  }

  const templatesLink = (
    <a className="cfo-shell-chip" href={`/${encodeURIComponent(navigation.siteSlug)}/firm${templatesOpen ? '' : '/templates'}`}>
      {templatesOpen ? 'Firm cockpit' : 'Templates'}
    </a>
  )

  const companySwitcher = companyOptions.length ? (
    <CfoShellSelectChip
      label="Select company"
      options={companyOptions}
      value={navigation.selectedWorkbookId}
      onChange={navigation.selectWorkbook}
      ariaLabel="Select company for CFO Core modules"
      menuAlign="end"
      className="cfo-firm-company-switcher"
    />
  ) : null

  return (
    <CfoPageShell
      activeNavId="firm"
      onNavSelect={openSelectedModule}
      brandLabel="CFO Core"
      brandSub={navigation.portfolio?.site_name || 'Firm cockpit'}
      crumb={<><b>Firm cockpit</b><span> · All companies</span></>}
      asOfLabel=""
      comparisonLabel=""
      showTopbar={false}
      dataSurface="firm_cockpit"
      navProfile="lightweight"
    >
      {templatesOpen ? (
        <section style={{ padding: 24 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><h2 style={{ margin: 0 }}>Templates</h2>{companySwitcher}{templatesLink}</div>
          <TemplatesPage api={viewsApi} workbookId={navigation.selectedWorkbookId || null} />
        </section>
      ) : (
        <CfoCockpitPage
          embedded
          headerActions={<>{companySwitcher}{templatesLink}</>}
          selectedWorkbookId={navigation.selectedWorkbookId}
          onCompanySelect={(client) => navigation.selectWorkbook(client.workbook_id)}
        />
      )}
    </CfoPageShell>
  )
}

export function CfoFirmCockpitApp({ topInsetPx = 0 }: { topInsetPx?: number }) {
  const api = useMemo(() => new ServerApiClient(), [])
  const siteSlug = useMemo(() => siteSlugFromLocation(), [])
  return (
    <div style={{ height: '100vh', minHeight: 0, paddingTop: topInsetPx, boxSizing: 'border-box' }}>
      <CfoFirmNavigationProvider api={api} siteSlug={siteSlug} loadPortfolio>
        <CfoFirmCockpitShell />
      </CfoFirmNavigationProvider>
    </div>
  )
}
